'use client'

import { useQuery } from '@tanstack/react-query'
import { CirclePlus, Info, RefreshCcw } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle, Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { BillingType, SeatPool } from './util'
import { useAuthClient, useSharedTRPC } from '../../context'
import { grantGroupLabel, seatSourceLabel } from '../../lib/seat-source'
import { ForeignAccessNotice } from './foreign-access-notice'
import { checkoutPaths, poolSeatLabel } from './util'

interface PlanHeaderProps {
  type: BillingType
  /** Navigates inside the host app — receives an app-relative path such as `/checkout/add-on`. */
  onNavigate: (path: string) => void
  className?: string
}

/**
 * Header of `/pengaturan/tagihan`: what the organization is on, how many seats it holds, and when
 * they lapse.
 *
 * Purchased pools get one strip each rather than one collapsed summary. An organization may hold
 * several at once with different end dates — 50 seats to December alongside 20 more to March — and
 * collapsing them would have to pick a single date, hiding whichever lapses first. `getSeatPools`
 * returns them soonest-expiring first, so the seats about to run out lead.
 *
 * Grant seats are reported separately, in a notice under the strips. They carry the same premium
 * access but none of the same controls: a sponsor's window cannot be topped up or extended, so
 * putting them in a strip would offer two buttons that lead nowhere they can serve. An organization
 * carried only by a grant therefore reads "Premium" with a "Tingkatkan" CTA — the honest offer, and
 * the same one its expiry emails make — plus the notice saying which seats end when.
 */
export function PlanHeader({ type, onNavigate, className }: PlanHeaderProps) {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()

  const { data: activeMemberRole } = authClient.useActiveMemberRole()

  // School teachers (role `member`) may read billing but never act on it — same split as Detail
  // Tagihan. Family accounts have no such split, so the buyer is always the reader.
  const canBuySeats = type === 'web' || activeMemberRole?.role !== 'member'

  /**
   * Scheduled pools included, which is what a renewal buys: a term that starts the day the live one
   * ends. Reading only live pools left this page showing the OLD end date the moment a school
   * renewed — the seats it had just paid for were invisible until the handover.
   */
  const {
    data: pools,
    isPending,
    isError,
    refetch,
  } = useQuery(trpc.myOrganization.getSeatPoolsWithScheduled.queryOptions())

  /**
   * Premium this organization holds on somebody else's seats — a child it shares with a family that
   * subscribes, through Sambung Sekolah-Rumah.
   *
   * Read here, not only inside `ForeignAccessNotice`, because it decides the PLAN WORD: an
   * organization whose students are all covered by their parents has premium access and an empty
   * pool list, and calling that "Starter" contradicts what the same screen says two lines down.
   * Same query key as the notice, so it costs no extra round trip.
   */
  const { data: foreignCoverage } = useQuery(trpc.myOrganization.getForeignPremiumCoverage.queryOptions())

  const hasForeignCoverage = (foreignCoverage?.length ?? 0) > 0

  if (isError) {
    return (
      <section
        className={cn(
          'srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-4 srd:rounded-xl srd:bg-grey-20 srd:p-4',
          className,
        )}
      >
        <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100 srd:text-center')}>
          Ada error yang terjadi saat mengambil data Premium
        </p>

        <Button variant='outline' onClick={() => void refetch()}>
          <RefreshCcw className='srd:mr-2 srd:size-4 srd:text-blue-100' />
          Coba lagi
        </Button>
      </section>
    )
  }

  if (isPending) {
    return <Skeleton className={cn('srd:h-[68px] srd:w-full srd:rounded-xl', className)} />
  }

  // Purchased and granted seats are surfaced differently, so they are split before anything renders.
  // `isCommercial` — not `source` — is the line that matters: an offline-paid `direct_paid`
  // entitlement was bought like any other plan and belongs with the purchases.
  const paidPools = pools.filter((pool: SeatPool) => pool.isCommercial)
  const grantPools = pools.filter((pool: SeatPool) => !pool.isCommercial)

  /**
   * The day paid Premium actually stops — the LAST end date across the purchased pools, not each
   * pool's own.
   *
   * A renewal leaves the organization holding a chain: the term running now, and the one queued
   * behind it. Printing the live pool's date beside a plan the school has already extended answers
   * a question nobody asked; what it wants to know is when it next has to act.
   */
  const paidEndsAt = paidPools.reduce<Date | null>(
    (latest, pool) => (!latest || pool.endDate > latest ? pool.endDate : latest),
    null,
  )

  /**
   * Seats across every purchased pool, terms that have not started included — the same reading as
   * the end date above, so the strip describes one subscription rather than one window of it.
   */
  const paidSeats = paidPools.reduce((sum, pool: SeatPool) => sum + pool.seatsTotal, 0)

  // Strips are still one per LIVE pool: a scheduled pool has no seats to hand out yet, and listing
  // it separately would show a school two plans when it has one that runs longer.
  const livePaidPools = paidPools.filter((pool: SeatPool) => !pool.isScheduled)
  const visiblePaidPools = livePaidPools.length > 0 ? livePaidPools : paidPools

  const addSeatsButton = canBuySeats ? (
    <Button
      variant='ghost'
      size='icon'
      className='srd:size-7'
      aria-label='Tambah kursi'
      onClick={() => onNavigate(checkoutPaths.addOn)}
    >
      <CirclePlus className='srd:size-5 srd:text-blue-100' />
    </Button>
  ) : null

  const renewButton = canBuySeats ? (
    <Button
      variant='ghost'
      size='icon'
      className='srd:size-7'
      aria-label='Perpanjang kursi'
      onClick={() => onNavigate(checkoutPaths.renewal)}
    >
      <CirclePlus className='srd:size-5 srd:text-blue-100' />
    </Button>
  ) : null

  const upgradeButton = canBuySeats ? (
    <Button size='sm' onClick={() => onNavigate(checkoutPaths.newPlan)}>
      Tingkatkan
    </Button>
  ) : null

  return (
    <div className={cn('srd:flex srd:w-full srd:flex-col srd:gap-y-4', className)}>
      {visiblePaidPools.length > 0 ? (
        visiblePaidPools.map((pool: SeatPool) => (
          <PlanStrip
            key={pool.id}
            plan='Premium'
            seatLabel={poolSeatLabel(pool.sourceType, pool.isCommercial)}
            seats={paidSeats}
            expiry={formatDate(paidEndsAt ?? pool.endDate, { dateStyle: 'long' })}
            seatsAction={addSeatsButton}
            expiryAction={renewButton}
          />
        ))
      ) : (
        /**
         * Nothing paid for: a grant carrying the organization, premium reaching it through someone
         * else's seats, or genuinely nothing (Starter). All three collapse to the plan name and one
         * CTA — there is no paid seat count to show and no window to extend, so the seat and expiry
         * columns would only print placeholders.
         *
         * Foreign coverage counts as Premium here. It is access this organization really has, and
         * the strip answers "what does this organization have right now", not "what has it bought" —
         * that second question is `isCommercialPremium`, which is what still decides
         * Perpanjang-vs-Jadi-Premium.
         */
        <PlanOnlyStrip
          plan={pools.length === 0 && !hasForeignCoverage ? 'Starter' : 'Premium'}
          action={upgradeButton}
        />
      )}

      {grantPools.length > 0 && <GrantSeatsNotice pools={grantPools} />}

      {/* Below the pools, because it describes access this organization does NOT hold a pool for —
          seats another organization provisioned for a child both of them share. It renders nothing
          unless such coverage exists, which for most organizations is always.

          Suppressed when the organization owns no pool at all: the strip above already reads
          Premium on the strength of exactly this coverage, so the notice would be explaining the
          line directly above it rather than reconciling two numbers that disagree. Where the
          organization DOES own pools it stays, because there it explains why the premium student
          count runs ahead of the seats the organization paid for. */}
      {pools.length > 0 && <ForeignAccessNotice type={type} onNavigate={onNavigate} />}
    </div>
  )
}

interface PlanOnlyStripProps {
  plan: string
  action: React.ReactNode
}

/** The plan on its own, with a single CTA — used when no purchased pool exists to describe. */
function PlanOnlyStrip({ plan, action }: PlanOnlyStripProps) {
  return (
    <section className='srd:flex srd:w-full srd:flex-wrap srd:items-end srd:justify-between srd:gap-4'>
      <Field label='Layanan saat ini' value={plan} />
      {action}
    </section>
  )
}

interface GrantSeatsNoticeProps {
  pools: SeatPool[]
}

/**
 * What the organization's grant seats cover, one line per pool. Pools are listed rather than summed:
 * each carries its own end date, and a single total would have to hide all but one of them.
 *
 * Every line names its own flavour, and the title only says "Beasiswa" when every pool in the group
 * actually is one. A directly provisioned or pilot pool announced as a scholarship is a claim about
 * where a school's funding came from, which is wrong often enough to matter.
 */
function GrantSeatsNotice({ pools }: GrantSeatsNoticeProps) {
  return (
    // The icon rides inside the title rather than filling `Alert`'s own icon slot. That slot is
    // driven by `has-[>svg]:grid-cols-[calc(var(--spacing)*5)_1fr]`, and `--spacing` does not exist
    // under the `ui` prefix — it is emitted as `--ui-spacing` — so the calc is invalid and the icon
    // track collapses. With no direct `svg` child the grid stays on its valid `[0_1fr]` fallback and
    // both slots land where `col-start-2` puts them.
    <Alert variant='neutral'>
      <AlertTitle className='srd:flex srd:items-center srd:gap-x-2'>
        <Info className='srd:size-5 srd:shrink-0' aria-hidden />
        {grantGroupLabel(pools.map((pool: SeatPool) => pool.sourceType))}
      </AlertTitle>

      <AlertDescription className='srd:flex srd:flex-col'>
        {pools.map((pool: SeatPool) => (
          <span key={pool.id}>
            {seatSourceLabel(pool.sourceType, pool.isCommercial)} — {pool.seatsTotal} kursi berakhir pada{' '}
            {formatDate(pool.endDate, { dateStyle: 'long' })}
          </span>
        ))}
      </AlertDescription>
    </Alert>
  )
}

interface PlanStripProps {
  plan: string
  /** Names the seats the count refers to — see `poolSeatLabel`. */
  seatLabel: string
  seats: number
  expiry: string
  /** Buys more seats on this pool's window. */
  seatsAction: React.ReactNode
  /** Extends the window itself. */
  expiryAction?: React.ReactNode
}

/** One purchased pool as a three-column labelled strip: plan, seats held, expiry. */
function PlanStrip({ plan, seatLabel, seats, expiry, seatsAction, expiryAction }: PlanStripProps) {
  return (
    <section className='srd:grid srd:w-full srd:grid-cols-1 srd:gap-x-4 srd:gap-y-3 srd:sm:grid-cols-3'>
      <Field label='Layanan saat ini' value={plan} />

      <Field label={seatLabel} value={String(seats)} action={seatsAction} />

      <Field label='Berakhir' value={expiry} action={expiryAction} />
    </section>
  )
}

interface FieldProps {
  label: string
  value: string
  action?: React.ReactNode
}

/** One labelled column: grey caption over its value, with an optional control beside the value. */
function Field({ label, value, action }: FieldProps) {
  return (
    <div className='srd:flex srd:min-w-0 srd:flex-col srd:gap-y-0.5'>
      <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-grey-100')}>{label}</p>

      <div className='srd:flex srd:min-h-7 srd:items-center srd:gap-x-1'>
        <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black srd:font-bold')}>
          {value}
        </p>
        {action}
      </div>
    </div>
  )
}
